import { BadgePercent, Box, ClipboardList, Earth, Fuel, Globe, GraduationCap, LayoutDashboard, Mail, MapPin, MessageCircle, PartyPopper, PhoneCall, ShoppingCart, Speech, Stethoscope, Store, Truck, UsersRound, Warehouse } from 'lucide-react';
import { FilterType } from 'bindings/filters/FilterType';
import { ValueKey } from 'bindings/reports/generic/ValueKey';
import close from 'images/icons/orders/close.svg';
import * as React from 'react';
import CompaniesTemplateEn from 'templates/import_companies_en.csv';
import CompaniesTemplateFr from 'templates/import_companies_fr.csv';
import { Cookies } from 'react-cookie';
import { getTranslate, Translate } from 'react-localize-redux';
import { useHistory } from 'react-router';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ITag } from '../../../typings/proto/protobufs';
import { AdditionalColumn, AVisibleAdditionalColumns, getFirstValueNotPieChart } from '../../atoms/additionalColumns';
import { AdditionalFieldColumn, AVisibleAdditionalFieldColumns, isFieldTypeSortable } from '../../atoms/additionalFieldColumns';
import { AAssortments } from '../../atoms/assortment';
import { AVisibleCalculatedFields, CalculatedField } from '../../atoms/calculatedFields';
import { ACompanyEdition } from '../../atoms/company/companyEdition';
import { AFormTemplate } from '../../atoms/forms';
import { AEventTypes, AtomEventType } from '../../atoms/global/events';
import { AFrequencyEventType } from '../../atoms/global/frequency';
import { AGranularity } from '../../atoms/global/granularity';
import { AExternalMappings, ASelectedExternalMappingId } from '../../atoms/global/mappings';
import { ASelectedFormId } from '../../atoms/global/selectedForm';
import { AUsers } from '../../atoms/global/users';
import { CompanyView, DbView, ViewCompanyColumns } from '../../atoms/global/views';
import Add from '../../components_v2/add/Add';
import AddressModificator from '../../components_v2/address/AddressModificator';
import ClientCompany from '../../components_v2/clientCompany/ClientCompany';
import ClientCompanyCreation from '../../components_v2/creation/ClientCompanyCreation';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import {
	FilterId,
	FilterResult,
	isCheckoutEvolutionFilter,
	isExternalIdFilter,
	isFrequencyFilter,
	isLastEventDateFilter,
	isLastFormDateFilter,
	isNextEventDateFilter,
	filterTreeLength,
	mergeFilterTrees
} from '../../components_v2/filter/model/Model';
import { compareSimilarFilters } from '../../components_v2/filter/pages/FilterList';
import PageLoader from '../../components_v2/pageLoader/PageLoader';
import { PaginationResult } from '../../components_v2/pagination/model/Model';
import Pagination from '../../components_v2/pagination/Pagination';
import { PopupMode } from '../../components_v2/popup/model/Model';
import Popup from '../../components_v2/popup/Popup';
import PopupCreation, { ButtonStyle } from '../../components_v2/popup/PopupCreation';
import PopupDeletion from '../../components_v2/popup/PopupDeletion';
import PopupInformation from '../../components_v2/popup/PopupInformation';
import TableSort, { OrderBy, TableSortColumn } from '../../components_v2/sort/TableSort';
import { Column, Table } from '../../components_v2/table/Table';
import { getUrlParameterByName, isSuperAdmin, removeAllUrlParameters } from '../../components_v2/utils';
import storeLang from '../../helpers/storeLang';
import { getLanguageCode } from '../reports/utils';
import { AiButton } from '../../styles/global/css/GlobalButton';
import { DefaultTextDiv } from '../../styles/global/css/GlobalText';
import { SidelyBlack } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { AlertContext } from '../alert/AlertProvider';
import { colorTypeMapping } from '../calendar/model/Model';
import PopupEvent from '../calendar/popup/Event';
import { InstancePopup } from '../forms/formInstancePopup';
import { ToolbarBox, ToolbarImageContainer } from '../globals/defaultToolbar/style/Style';
import { ToolBarStateWithLeftAndRight } from '../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../import/model';
import { ReactStateType } from '../map/Map';
import { ComponentLoader } from '../map/modalRight/ModalCalendar';
import NoData from '../noData/NoData';
import PermissionContext from '../permissions/PermissionContext';
import Restricted from '../permissions/Restricted';
import { ModalState } from '../products/model';
import { FlexDiv, Link } from '../products/style';
import { mapColumnFilterIds } from './CompaniesAndMapView';
import { deleteCompanies, getCompanies, updateCompany as putCompany } from './data/action';
import CompanyColumns, { filterIdToTranslatedName } from './data/CompanyColumns';
import { Company, CompanyOrderBy, CompanyParams, CompanyStatus, DbCompany } from './model/Model';
import { enrichCompanies, RejectedValueItem } from './popup/action';
import { Column as OrganizerColumn } from './popup/ColumnOrganizer';
import CompanyBulkEdition from './popup/CompanyBulkEdition';
import CompanyBulkEventCreation from './popup/CompanyBulkEventCreation';
import GeoPopupContent from './popup/GeoPopup';
import { Container, SelectAllDiv } from './style/Style';
import BottomActionButtons from './BottomActionButtons';

export function additionalColumnIdFormatter(id: unknown, path?: ValueKey) {
	return JSON.stringify({
		additional_columns: [{ id, path, name: false }]
	});
}

export function calculatedFieldIdFormatter(id: unknown, path?: ValueKey) {
	return JSON.stringify({
		calculated_field_columns: [{ id, path, name: false }]
	});
}

export function additionalFieldColumnIdFormatter(id: unknown) {
	return JSON.stringify({
		additional_field_columns: [id]
	});
}

export const filterCategories = ['most_used_filters', 'company_informations', 'company_activity', 'events', 'forms', 'shelf_audit', 'checkouts'] as const;
export type FilterCategory = typeof filterCategories[number];
export const COMPANY_COLUMN_CATEGORIES_MAP: { [key: string]: number } = [...filterCategories, 'report_fields', 'additional_field_columns', 'additional_columns', 'calculated_field_columns'].reduce((acc, cat, i) => {
	acc[cat] = i;
	return acc;
}, {});

export type ColumnResume = {
	translationKey?: string,
	name: string,
	excluded?: boolean,
	hidded?: boolean,
	admin?: boolean,
	filterCategories?: FilterCategory[],
	noTranslation?: boolean,
	fixedLeft?: boolean,
	categoryIndex?: number
}

export const checkUrlModalParams = (f: (state: { isOpen: boolean, id: string, isFullOpen: boolean }) => void, idName?: string) => () => {
	const id = getUrlParameterByName(idName ?? 'id');
	const mode = getUrlParameterByName('mode');
	let isFullOpen = true;
	if (id) {
		switch (mode) {
			case 'detail':
				isFullOpen = false;
				break;
		}
		f({ isOpen: true, isFullOpen, id });
	}
};

// Row selection of the companies table, as exposed to embedding popups:
// isAllSelected means every filtered company except the unchecked ones
export type CompanySelection = {
	isAllSelected: boolean,
	checked: number[],
	unchecked: number[],
}

export const getCompaniesFitlersCategories = (filterId: FilterId): FilterCategory[] => {
	if (isCheckoutEvolutionFilter(filterId)) return ['checkouts'];
	if (isFrequencyFilter(filterId)) {
		return ['most_used_filters', 'events'];
	}
	if (isLastEventDateFilter(filterId) || isNextEventDateFilter(filterId)) {
		return ['events'];
	}
	if (isExternalIdFilter(filterId)) {
		return ['company_informations'];
	}
	if (isLastFormDateFilter(filterId)) {
		return ['most_used_filters', 'forms'];
	}
	switch (filterId) {
		case 'name':
		case 'company_id':
		case 'external_id':
		case 'email':
		case 'phone':
		case 'website':
		case 'company_address':
		case 'company_country':
		case 'latitude':
		case 'longitude':
			return ['company_informations'];
		case 'company_city':
		case 'company_post_code':
		case 'status_id':
		case 'owner_id':
		case 'tag_id':
			return ['most_used_filters', 'company_informations'];
		case 'created_at':
		case 'created_by':
		case 'updated_at':
		case 'updated_by':
			return ['company_activity'];
		case 'dn':
		case 'last_shelf_audit_date':
			return ['shelf_audit'];
		case 'checkout_evolution':
		case 'checkout_previous_sum':
		case 'checkout_current_sum':
		case 'checkout_current_uvc_sum':
		case 'checkout_previous_uvc_sum':
		case 'checkout_uvc_evolution':
			return ['checkouts'];
		case 'last_event_date':
		case 'next_event_date':
		case 'frequency_event':
		case 'frequency_event_target':
		case 'frequency_next_date':
		case 'frequency_remaining_days':
			return ['events'];
		case 'shelf_audit_templates':
			return ['shelf_audit'];
		case 'last_form_date':
			return ['forms'];
	}
	return [];
};

export function getImageFromEventTypeName(name: string, white = false): JSX.Element {
	switch (name) {
		case 'Task': return <ClipboardList color={white ? 'white' : SidelyBlack} />;
		case 'Call': return <PhoneCall color={white ? 'white' : SidelyBlack} />;
		case 'Meeting': return <Speech color={white ? 'white' : SidelyBlack} />;
		case 'Visit': return <MapPin color={white ? 'white' : SidelyBlack} />;
		case 'SMS': return <MessageCircle color={white ? 'white' : SidelyBlack} />;
		case 'Email': return <Mail color={white ? 'white' : SidelyBlack} />;
		case 'Promotion': return <BadgePercent color={white ? 'white' : SidelyBlack} />;
		case 'Animation': return <Store color={white ? 'white' : SidelyBlack} />;
		case 'Delivery': return <Truck color={white ? 'white' : SidelyBlack} />;
		case 'Order': return <ShoppingCart color={white ? 'white' : SidelyBlack} />;
		case 'TimeOff': return <Earth color={white ? 'white' : SidelyBlack} />;
		case 'PublicHoliday': return <PartyPopper color={white ? 'white' : SidelyBlack} />;
		case 'Session': return <UsersRound color={white ? 'white' : SidelyBlack} />;
		case 'Training': return <GraduationCap color={white ? 'white' : SidelyBlack} />;
		case 'TradeShow': return <Globe color={white ? 'white' : SidelyBlack} />;
		case 'SickLeave': return <Stethoscope color={white ? 'white' : SidelyBlack} />;
		case 'WarehouseVisit': return <Warehouse color={white ? 'white' : SidelyBlack} />;
		case 'VehicleMaintenance': return <Fuel color={white ? 'white' : SidelyBlack} />;
		case 'SampleCollection': return <Box color={white ? 'white' : SidelyBlack} />;
		case 'StoreLayout': return <LayoutDashboard color={white ? 'white' : SidelyBlack} />;
	}
	throw 'Unknown event type';
}

const ALL_COMPANIES_COLUMNS: ColumnResume[] = [
	{ name: 'select', excluded: true },
	{ name: 'name', filterCategories: ['company_informations'], excluded: true },
	{ name: 'company_id', filterCategories: ['company_informations'], hidded: true },
	{ name: 'external_id', filterCategories: ['company_informations'], hidded: true },
	{ name: 'email', filterCategories: ['company_informations'] },
	{ name: 'phone', filterCategories: ['company_informations'] },
	{ name: 'website', filterCategories: ['company_informations'] },
	{ name: 'company_address', filterCategories: ['company_informations'] },
	{ name: 'company_city', filterCategories: ['most_used_filters', 'company_informations'] },
	{ name: 'company_post_code', filterCategories: ['most_used_filters', 'company_informations'] },
	{ name: 'company_country', filterCategories: ['company_informations'] },
	{ name: 'latitude', filterCategories: ['company_informations'] },
	{ name: 'longitude', filterCategories: ['company_informations'] },
	{ name: 'status_id', filterCategories: ['most_used_filters', 'company_informations'] },
	{ name: 'owner_id', filterCategories: ['most_used_filters', 'company_informations'] },
	{ name: 'tag_id', filterCategories: ['most_used_filters', 'company_informations'] },
	{ name: 'created_at', filterCategories: ['company_activity'] },
	{ name: 'created_by', filterCategories: ['company_activity'], hidded: true },
	{ name: 'updated_at', filterCategories: ['company_activity'], hidded: true },
	{ name: 'updated_by', filterCategories: ['company_activity'], hidded: true },
	{ name: 'last_event_date', filterCategories: ['events'] },
	{ name: 'next_event_date', filterCategories: ['events'] },
	{ name: 'frequency_event', filterCategories: ['events'] },
	{ name: 'frequency_event_target', filterCategories: ['events'] },
	{ name: 'frequency_next_date', filterCategories: ['events'], hidded: true },
	{ name: 'frequency_remaining_days', filterCategories: ['events'], hidded: true },
	{ name: 'last_form_date', filterCategories: ['shelf_audit'], hidded: true },
	{ name: 'quick_actions', excluded: true }
];

export function allCompaniesAndAdditionalColumns(additionalColumns: AdditionalColumn[], additionalFieldColumns: AdditionalFieldColumn[], calculatedFields: CalculatedField[]) {
	return [
		...ALL_COMPANIES_COLUMNS,
		...additionalColumns.map((ac): ColumnResume => ({ name: additionalColumnIdFormatter(ac.id) })),
		...additionalFieldColumns.map((ac): ColumnResume => ({ name: additionalFieldColumnIdFormatter(ac.field_id) })),
		...calculatedFields.map((ac): ColumnResume => ({ name: calculatedFieldIdFormatter(ac.id) }))
	];
}

function filterIdToTranslatedNameWithAdditionalColumns(filterId: FilterId, additionalColumns: AdditionalColumn[], additionalFieldColumns: AdditionalFieldColumn[], calculatedFields: CalculatedField[]): string {
	if (typeof filterId === 'string') return filterIdToTranslatedName(filterId);
	if ('additional_columns' in filterId)
		return additionalColumns.find(ac => ac.id == filterId.additional_columns[0].id)?.name ?? '';
	if ('additional_field_columns' in filterId)
		return additionalFieldColumns.find(ac => ac.field_id == filterId.additional_field_columns[0])?.field_name ?? '';
	if ('calculated_field_columns' in filterId)
		return calculatedFields.find(ac => ac.id == filterId.calculated_field_columns[0].id)?.field_name ?? '';
	return filterIdToTranslatedName(filterId);
}

const sortItems = (a: RejectedValueItem, b: RejectedValueItem) => (b.scoring?.queryScore ?? 0) - (a.scoring?.queryScore ?? 0);
export default function Companies(props: {
	allCompaniesColumnsAndAdditionalColumns: ColumnResume[],
	setToolBarState: (value: ToolBarStateWithLeftAndRight) => void,
	filterResult: FilterResult,
	selectedView: DbView<CompanyView> | undefined,
	setSelectedFilter: React.Dispatch<React.SetStateAction<FilterId | undefined>>,
	setOpenSummary: React.Dispatch<React.SetStateAction<boolean>>,
	setFilterOpen: React.Dispatch<React.SetStateAction<boolean>>,
	setHiddenColumns: React.Dispatch<React.SetStateAction<string[]>>,
	hiddenColumns: string[],
	setSortColumns: React.Dispatch<React.SetStateAction<string[]>>,
	sortColumns: string[],
	allColumns: { id: FilterId, name: string }[],
	statuses: CompanyStatus[]
	tags: ITag[]
	eventTypes: AtomEventType[]
	searchName: string | undefined,
	onResetFilters?: () => void,
	setHidableColumns: React.Dispatch<React.SetStateAction<OrganizerColumn[]>>,
	height?: string,
	companies?: number[],
	setBody?: (body: CompanyParams) => void,
	onSelectionChange?: (selection: CompanySelection) => void,
	orderBysHook: ReactStateType<OrderBy<typeof props['allColumns'][number]>[]>,
	companyListingPopupOpenState: ReactStateType<ModalState<number[]>>,
	isPopup?: boolean
}): JSX.Element {
	const DEFAULT_STEP = 25;
	const DEFAULT_OFFSET = 0;
	const { searchName, statuses, tags, filterResult, selectedView, setSelectedFilter, setOpenSummary, setFilterOpen, setHiddenColumns, hiddenColumns, setSortColumns, sortColumns, allColumns } = props;
	const translate = getTranslate(storeLang.getState().localize);

	const [isOpen, setOpen] = React.useState<boolean>(false);
	const [isFullOpenMode, setFullOpenMode] = React.useState<boolean>(true);

	const [rawCompanies, setRawCompanies] = React.useState<DbCompany>({ total: 0, companies: [] });
	const [companies_, setCompanies_] = React.useState<Company[]>([]);
	const companies = React.useRef(companies_);
	const setCompanies = (e: Company[]) => {
		companies.current = e;
		setCompanies_(e);
	};

	const [clientCompanyId, setClientCompanyId] = React.useState<number>();
	const [pagination_, setPagination_] = React.useState<PaginationResult>();
	const pagination = React.useRef(pagination_);
	const setPagination = (e: PaginationResult | undefined) => {
		pagination.current = e;
		setPagination_(e);
	};
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>();

	const [isCreationOpen, setCreationOpen] = React.useState<boolean>(false);
	const [selectedDuplicateCompany, setSelectedDuplicateCompany] = React.useState<Company>();
	const [isBulkEditOpen, setIsBulkEditOpen] = React.useState<boolean>(false);
	const [isBulkDeleteOpen, setIsBulkDeleteOpen] = React.useState<boolean>(false);

	const [isAllSelected, setIsAllSelected_] = React.useState<boolean>(false);
	const setIsAllSelected = (b: boolean) => {
		setIsAllSelected_(b);
		if (!b) {
			setCompanies(companies.current.map(c => ({ ...c, checked: false })));
			setSelectedCompanies([]);
		} else {
			setCompanies(companies.current.map(c => ({ ...c, checked: true })));
		}
	};
	const [selectedEventType, setSelectedEventType] = React.useState<AtomEventType>();
	const [isParent, setIsParent] = React.useState<boolean>(false);
	const isAdmin = isSuperAdmin();

	const [selectedCompanies, setSelectedCompanies] = React.useState<Array<{ id: number, checked?: boolean }>>([]);

	React.useEffect(() => {
		props.onSelectionChange?.({
			isAllSelected,
			checked: selectedCompanies.filter(c => c.checked).map(c => c.id),
			unchecked: selectedCompanies.filter(c => !c.checked).map(c => c.id),
		});
	}, [JSON.stringify(selectedCompanies), isAllSelected]);
	const formTemplates = useRecoilValue(AFormTemplate).instanceForms;

	const [isModifyAddressOpen, setModifyAddressOpen] = React.useState<boolean>(false);
	const [modifyAddress, setModifyAddress] = React.useState<Company>();
	const atomCompanyEdition = useRecoilValue(ACompanyEdition);
	const atomEventTypes = useRecoilValue(AEventTypes);
	const [selectedColumnEventType, setSelectedColumnEventType] = useRecoilState(AFrequencyEventType);
	const [selectedColumnFormId, setSelectedColumnFormId] = useRecoilState(ASelectedFormId);
	const atomExternalMapping = useRecoilValue(AExternalMappings);
	const [selectedExternalMappingId, setSelectedExternalMappingId] = useRecoilState(ASelectedExternalMappingId);
	const granularity = useRecoilValue(AGranularity);

	const [informationModalState, setInformationModalState] = React.useState<ModalState<{ content: JSX.Element, title: JSX.Element, hideButton?: boolean }>>({ isOpen: false });
	const [eventModalState, setEventModalState] = React.useState<ModalState<number>>({ isOpen: false });
	const [isEnrichOpen, setIsEnrichOpen] = React.useState<boolean>(false);
	const [reload, setReload] = React.useState<boolean>(false);
	const [lastRequest, setLastRequest] = React.useState<string>();

	const [tableResize, setTableResize] = React.useState(JSON.parse(localStorage.getItem('table_company_resize') || '{}'));
	const additionalColumns = useRecoilValue(AVisibleAdditionalColumns);
	const additionalFieldColumns = useRecoilValue(AVisibleAdditionalFieldColumns);
	const calculatedFields = useRecoilValue(AVisibleCalculatedFields);
	const [formModalState, setFormModalState] = React.useState<ModalState<string>>({ isOpen: false });

	const cookie = new Cookies();
	const history = useHistory();
	const users = useRecoilValue(AUsers);
	const { alertDelete } = React.useContext(AlertContext);
	const { isAllowedTo } = React.useContext(PermissionContext);
	const assortments = useRecoilValue(AAssortments);

	const tableRef = React.useRef<HTMLDivElement>();
	const [orderBys, setOrderBys] = props.orderBysHook;

	const eventTypesDropdownValues = React.useMemo(() => colorTypeMapping.map((c) => ({ label: translateToString('event.' + c.label), value: c.id })), []);
	const formTemplatesDropdownValues = React.useMemo(() => formTemplates.map((t) => ({ label: t.name, value: t.id })), [formTemplates]);

	React.useEffect(() => {
		if (atomCompanyEdition != null) {
			if ('deleted' in atomCompanyEdition) {
				const index = companies.current.findIndex(c => c.company_id == atomCompanyEdition.deleted);
				companies.current.splice(index, 1);
				setCompanies([...companies.current]);
				return;
			}
			const index = companies.current.findIndex(c => c.company_id == atomCompanyEdition.client_company_id);
			if (index >= 0) {
				for (const [key, value] of Object.entries(atomCompanyEdition)) {
					switch (key) {
						case 'latitude':
							companies.current[index].latitude = value;
							continue;
						case 'longitude':
							companies.current[index].longitude = value;
							continue;
						case 'email':
							companies.current[index].email = value;
							continue;
						case 'owner_id': {
							const user = users.find(u => u.id == value);
							if (user != null) {
								companies.current[index].owner_id = user.id;
								companies.current[index].owner_name = user.name;
								companies.current[index].owner_photo = user.photoUrl;
							}
							continue;
						}
						case 'phone':
							companies.current[index].phone = value;
							continue;
						case 'website':
							companies.current[index].website = value;
							continue;
						case 'company_name':
							companies.current[index].name = value;
							continue;
						case 'client_status_id': {
							const status = statuses.find(s => s.id === value);
							if (status != null) {
								companies.current[index].status_id = status.id;
								companies.current[index].company_status_color = status.color_code;
								companies.current[index].company_status_name = status.name;
							}
							continue;
						}
						// TAGS
						case 'ids_add':
							companies.current[index].tags = companies.current[index].tags.concat(tags.filter(t => value.includes(t.id)));
							continue;
						case 'ids_delete':
							companies.current[index].tags = companies.current[index].tags.filter(t => !value.includes(t.id));
							continue;
					}
				}
				companies.current[index].updated_at = new Date();
				companies.current[index].updated_by = users.find(u => u.isYou)?.id;
				setCompanies([...companies.current]);
			}
		}
	}, [atomCompanyEdition]);

	React.useEffect(() => {
		const f = ({ isOpen, isFullOpen, id }) => {
			const parsedId = parseInt(id);
			if (isNaN(parsedId)) return;
			setOpen(isOpen);
			setFullOpenMode(isFullOpen);
			setClientCompanyId(parsedId);
		};
		checkUrlModalParams(f)();
		history.listen(checkUrlModalParams(f));
	}, [history]);

	React.useEffect(() => {
		updateRawCompanies();
	}, [JSON.stringify(statuses), JSON.stringify(users), JSON.stringify(tags), JSON.stringify(rawCompanies)]);

	React.useEffect(() => {
		let newSelectedCompanies = [...selectedCompanies];

		newSelectedCompanies = newSelectedCompanies
			.map(c => {
				const company = companies.current.find(ac => ac.company_id == c.id);
				if (company != null) {
					return { id: company.company_id, checked: company.checked };
				} else {
					return c;
				}
			});

		companies.current.forEach(company => {
			if (!newSelectedCompanies.some(sc => sc.id == company.company_id)) {
				newSelectedCompanies.push({ id: company.company_id, checked: company.checked });
			}
		});
		setSelectedCompanies([...newSelectedCompanies]);
	}, [JSON.stringify(companies.current)]);

	function addAdditionalDropdownData(columnId: FilterId): DropdownData[] | undefined {
		if (typeof columnId === 'string') return undefined;
		if ('last_form_date' in columnId) return formTemplatesDropdownValues;
		if ('next_event_date' in columnId || 'last_event_date' in columnId || 'frequencies' in columnId) return eventTypesDropdownValues;
		return undefined;
	}

	function filterIdToTableSortColumn(filterId: FilterId): TableSortColumn<FilterId> | undefined {
		if (filterId === 'parent_id' || filterId === 'parent_name') return undefined;
		if (typeof filterId !== 'string' && 'additional_field_columns' in filterId) {
			const afcId = filterId.additional_field_columns[0];
			const afc = additionalFieldColumns.find(afc => afc.field_id === afcId);
			if (afc === undefined || !isFieldTypeSortable(afc.field_type)) return undefined;
		}
		return {
			name: filterIdToTranslatedNameWithAdditionalColumns(filterId, additionalColumns, additionalFieldColumns, calculatedFields),
			id: filterId,
			additionalDropdownData: addAdditionalDropdownData(filterId)
		};
	}
	React.useEffect(() => {
		props.setToolBarState({
			title: translate('companies') as string,
			beforeSaveView: <>
				<TableSort
					compareFn={compareSimilarFilters}
					defaultOrderBy={orderBys}
					columns={allColumns
						.reduce<TableSortColumn<FilterId>[]>((acc, c) => {
							const res = filterIdToTableSortColumn(c.id);
							if (res !== undefined) acc.push(res);
							return acc;
						}, [])
					}
					onSort={setOrderBys}
				/></>,
			rightBottomLeft: <></>,
			leftBottomRight: <>
				<Restricted to={{ objectAction: 'ViewQuickTspOptimization' }}>
					<AiButton
						innerStyle={{ flexShrink: 0 }}
						margin='0'
						onClick={() => props.companyListingPopupOpenState?.[1]?.({ isOpen: true })}
					>
						<Translate id='map.modal_right.optimize' />
					</AiButton>
				</Restricted>
			</>,
			rightBottomRight: <ToolbarBox>
				<Restricted to={{ objectAction: 'ReadCompany' }}>
					<Restricted to={{ objectAction: 'CreateCompany' }}>
						<ToolbarImageContainer
							hasPointer
							onClick={() => { setSelectedDuplicateCompany(undefined); setCreationOpen(true); }}
						>
							<Add size='22px' />
						</ToolbarImageContainer>
					</Restricted>
				</Restricted>
			</ToolbarBox>
		});
	}, [JSON.stringify(selectedCompanies), isAllSelected, searchName, hiddenColumns, sortColumns, allColumns, orderBys]);

	React.useEffect(() => {
		setPagination({ currentPage: 1, offset: DEFAULT_OFFSET, step: pagination?.current?.step ?? DEFAULT_STEP });
		setSelectedCompanies([]);
		setRawCompanies({ companies: [], total: -1 });
		setCompanies([]);
	}, [JSON.stringify({ filterResult, searchName })]);

	React.useEffect(() => {
		updateCompanies();
	}, [pagination.current, selectedColumnEventType, selectedColumnFormId, allColumns, hiddenColumns, granularity, orderBys]);

	const columnsToView = (): ViewCompanyColumns[] => sortColumns.map(c => ({
		column: c,
		hidden: hiddenColumns.includes(c)
	}));

	React.useEffect(() => {
		if (!selectedView) {
			const companyId = new Cookies().get('company_id');
			if (companyId) {
				const allStored: Record<string, ViewCompanyColumns[]> = JSON.parse(localStorage.getItem('companies_hidden_columns') || '{}');
				allStored[companyId] = columnsToView();
				localStorage.setItem('companies_hidden_columns', JSON.stringify(allStored));
			}
		}
	}, [JSON.stringify(hiddenColumns), JSON.stringify(sortColumns)]);

	React.useEffect(() => {
		localStorage.setItem('table_company_resize', JSON.stringify(tableResize));
	}, [tableResize]);

	function updateCompanies(force?: boolean): void {
		if (allColumns.length > 0 && loadingState !== LoadingStateEnum.LOADING || force) {
			getNewCompanies(force)
				.then(response => {
					if (!response) return;
					if (response.companies.length > 0 && (sortColumns.length <= 0 || sortColumns.length < Object.keys(response.companies[0]).length)) {
						setSortColumns(props.allCompaniesColumnsAndAdditionalColumns
							.filter(c => !c.excluded && (!c.admin || isAdmin))
							.sort((a, b) => {
								let indexA = sortColumns.findIndex(sc => sc == a.name);
								let indexB = sortColumns.findIndex(sc => sc == b.name);
								if (indexA < 0) {
									indexA = props.allCompaniesColumnsAndAdditionalColumns.findIndex(dcs => dcs.name == a.name);
									if (indexA < 0) indexA = 999;
								}
								if (indexB < 0) {
									indexB = props.allCompaniesColumnsAndAdditionalColumns.findIndex(dcs => dcs.name == b.name);
									if (indexB < 0) indexB = 999;
								}
								return indexA - indexB;
							})
							.map(c => c.name)
							.map(k => k === 'tags' ? 'tag_id' : k)
						);
					}
					setRawCompanies(response);
					if (reload) {
						setReload(false);
						updateCompanies();
					} else {
						setReload(false);
					}
				});
		} else if (allColumns.length > 0) {
			setReload(true);
		}
	}

	const createBody = (pagination: PaginationResult | undefined, order_by: string | object | undefined, order_by_type: FilterType | undefined, orderBys: CompanyOrderBy[] | undefined): CompanyParams => ({
		limit: pagination?.step,
		offset: pagination?.offset,
		new_filters: mergeFilterTrees(filterResult.formatted,
			(props.companies?.length ?? 0) > 0 ? {
				'val': { column: 'company_id', value: props.companies?.toString(), operator: 'contained' }
			} : undefined,
		),
		search: searchName,
		order_by,
		order_by_type,
		order_bys: orderBys,
		descending: false,
		columns: allColumns.filter(({ name }) => !hiddenColumns.includes(name)).reduce(mapColumnFilterIds(selectedColumnEventType, selectedExternalMappingId, granularity, selectedColumnFormId), [])
	});

	async function transformSortId(order_by: typeof orderBys[number]): Promise<CompanyOrderBy> {
		let res: FilterId = {} as FilterId;
		let order_by_type: FilterType | undefined;
		switch (order_by.order_by) {
			case 'last_event_date':
			case 'next_event_date':
				res[order_by.order_by] = order_by.additionalValue!;
				break;
			case 'external_id':
				res[order_by.order_by] = selectedExternalMappingId.company;
				break;
			case 'frequency_event':
				res['frequencies'] = { event: order_by.additionalValue!, element: 'frequency' };
				break;
			case 'frequency_event_target':
				res['frequencies'] = { event: order_by.additionalValue!, element: 'score' };
				break;
			case 'checkout_evolution':
				res['checkout'] = { element: 'evolution', granularity };
				break;
			case 'checkout_current_sum':
				res['checkout'] = { element: 'current_sum', granularity };
				break;
			case 'checkout_previous_sum':
				res['checkout'] = { element: 'previous_sum', granularity };
				break;
			case 'checkout_uvc_evolution':
				res['checkout'] = { element: 'uvc_evolution', granularity };
				break;
			case 'checkout_current_uvc_sum':
				res['checkout'] = { element: 'current_uvc_sum', granularity };
				break;
			case 'checkout_previous_uvc_sum':
				res['checkout'] = { element: 'previous_uvc_sum', granularity };
				break;
			case 'last_form_date':
				res[order_by.order_by] = order_by.additionalValue!;
				break;
			default:
				res = typeof order_by.order_by === 'string' ? order_by.order_by : { ...order_by.order_by };
		}
		if (typeof res !== 'string') {
			if (isFrequencyFilter(res)) {
				res.frequencies.event = order_by.additionalValue as number | undefined;
			} else if (isLastFormDateFilter(res)) {
				res.last_form_date = order_by.additionalValue as number | null;
			} else if (isNextEventDateFilter(res)) {
				res.next_event_date = order_by.additionalValue as number | null;
			} else if (isLastEventDateFilter(res)) {
				res.last_event_date = order_by.additionalValue as number | null;
			}
		}

		if (typeof order_by.order_by !== 'string' && 'calculated_field_columns' in order_by.order_by) {
			if ((order_by.order_by.calculated_field_columns as unknown[]).length > 0) {
				const cols = order_by.order_by.calculated_field_columns as { id: unknown }[];
				const allFirstValues = await getFirstValueNotPieChart();
				const calculatedFirstValues = allFirstValues.filter(fv => fv.column_type === 'calculated_field');
				const calculatedFirstValue = calculatedFirstValues.find(fv => fv.id === cols[0].id);
				if (calculatedFirstValue && calculatedFirstValue.value && 'percentage' in calculatedFirstValue.value) {
					order_by_type = 'percentage';
				}
			}
		}
		return { order_by: res, order_by_type, descending: order_by.descending };
	}

	async function getNewCompanies(force?: boolean): Promise<DbCompany | undefined> {
		if (pagination.current != null) {
			const order_bys = await Promise.all(orderBys.map(transformSortId));
			const body = createBody(pagination.current, undefined, undefined, order_bys);
			props.setBody?.(body);
			if (JSON.stringify(body) === lastRequest && !force) {
				return undefined;
			}
			setLoadingState(LoadingStateEnum.LOADING);
			setLastRequest(JSON.stringify(body));
			const response = await getCompanies(body);
			tableRef.current?.scrollTo({ top: 0, behavior: 'auto' });
			setLoadingState(LoadingStateEnum.LOADED);
			return response;
		} else {
			return await new Promise(() => ({ total: 0, companies: [] }));
		}
	}

	function updateRawCompanies(): void {
		setCompanies(rawCompanies.companies.map(c => {
			const newTags = c.tags.map(t => {
				const nt = tags.find(tt => tt.id === (t as unknown as number));
				return { id: (t as unknown as number), name: nt?.name ?? '', color: nt?.color ?? '' };
			});
			const owner = users.find(u => u.id === c.owner_id);
			const status = statuses.find(s => s.id == c.status_id);
			return {
				...c,
				company_status_name: status?.name,
				company_status_color: status?.color_code,
				owner_name: owner?.name ?? '',
				owner_photo: owner?.photoUrl ?? '',
				tags: newTags,
				checked: selectedCompanies.find(sc => sc.id == c.company_id)?.checked ?? isAllSelected
			} as Company;
		}));
	}

	const data: DropdownData[] = [];
	if (isAllowedTo({ objectAction: 'DeleteCompany' })) {
		data.push({ label: translate('company.toolbar.delete_selection').toString(), value: 'delete' });
	}

	const hidableColumns: OrganizerColumn[] = React.useMemo(() => {
		const columns = ALL_COMPANIES_COLUMNS.filter(c => !c.excluded && (!c.admin || isAdmin)).map(c => {
			let sequence = sortColumns.indexOf(c.name);
			if (sequence === -1) sequence = 999;
			const categoryIndex = COMPANY_COLUMN_CATEGORIES_MAP[getCompaniesFitlersCategories(c.name).find(c => c !== 'most_used_filters') ?? ''];
			return ({
				label: translateToString(`company.columns.${c.name}`),
				value: c.name,
				checked: !hiddenColumns.includes(c.name),
				sequence,
				categoryIndex
			});
		});
		additionalColumns.forEach(ac => {
			const id = additionalColumnIdFormatter(ac.id);
			let sequence = sortColumns.indexOf(id);
			if (sequence === -1) sequence = 999;
			if (ac.type !== 'ReportColumn') {
				columns.push({
					label: ac.name,
					value: id,
					checked: !hiddenColumns.includes(id),
					sequence,
					categoryIndex: COMPANY_COLUMN_CATEGORIES_MAP['additional_columns']
				});
			}
		});
		additionalFieldColumns.forEach(ac => {
			const id = additionalFieldColumnIdFormatter(ac.field_id);
			let sequence = sortColumns.indexOf(id);
			if (sequence === -1) sequence = 999;
			columns.push({
				label: ac.field_name,
				value: id,
				checked: !hiddenColumns.includes(id),
				sequence,
				categoryIndex: COMPANY_COLUMN_CATEGORIES_MAP['additional_field_columns']
			});
		});
		calculatedFields.forEach(cf => {
			const id = calculatedFieldIdFormatter(cf.id);
			let sequence = sortColumns.indexOf(id);
			if (sequence === -1) sequence = 999;
			columns.push({
				label: cf.field_name,
				value: id,
				checked: !hiddenColumns.includes(id),
				sequence,
				categoryIndex: COMPANY_COLUMN_CATEGORIES_MAP['calculated_field_columns']
			});
		});
		columns.sort((a, b) => a.sequence - b.sequence);
		return columns;
	}, [JSON.stringify(sortColumns), hiddenColumns, isAdmin, additionalColumns, additionalFieldColumns]);

	React.useEffect(() => {
		const columnsForOrganizer = hidableColumns.sort((a, b) => a.sequence - b.sequence);
		columnsForOrganizer.forEach((c, i) => {
			c.sequence = i;
		});
		props.setHidableColumns(columnsForOrganizer);
	}, [JSON.stringify(hidableColumns)]);

	function updateCompany(company: Company): void {
		company.updated_at = new Date();
		company.updated_by = cookie.get('user_id');
		setCompanies(companies.current.map(c => c.company_id === company.company_id ? company : c));
	}

	function deleteCompany(company: Company): void {
		setCompanies(companies.current.filter(c => c.company_id !== company.company_id));
	}

	function duplicateCompany(company: Company): void {
		setSelectedDuplicateCompany(company);
		setCreationOpen(true);
	}

	const columns: Array<Column<Company>> = React.useMemo(
		() => {
			const cols = CompanyColumns(
				(value, mode) => {
					history.push({
						search: `?id=${value}`
					});
					setFullOpenMode(!mode);
				},
				updateCompany,
				setCompanies,
				duplicateCompany,
				deleteCompany,
				(value) => { setModifyAddress(value); setModifyAddressOpen(true); },
				statuses,
				users,
				tags,
				companies.current.every(c => c.checked),
				hidableColumns,
				setSortColumns,
				setHiddenColumns,
				(checked) => setCompanies(companies.current.map(c => ({ ...c, checked }))),
				atomEventTypes,
				selectedColumnEventType,
				setSelectedColumnEventType,
				() => updateCompanies(true),
				data => setEventModalState({ isOpen: true, data }),
				loadingState,
				atomExternalMapping,
				selectedExternalMappingId,
				setSelectedExternalMappingId,
				alertDelete,
				isAllowedTo,
				additionalColumns,
				additionalFieldColumns,
				assortments,
				selectedColumnFormId,
				setSelectedColumnFormId,
				formTemplates,
				setFormModalState,
				calculatedFields,
				isAdmin
			);

			if (sortColumns.length > 0) {
				const newC = [cols[0], cols[1]];
				for (const col of cols) {
					if (col.id?.startsWith('ADMIN_TEST') && isAdmin) newC.push(col);
				}
				props.allCompaniesColumnsAndAdditionalColumns
					.filter(c => !c.excluded && (!c.admin || isAdmin))
					.map(column => {
						let sequence = sortColumns.indexOf(column.name);
						if (sequence === -1) sequence = 999;
						return ({
							column,
							sequence
						});
					})
					.sort((a, b) => a.sequence - b.sequence)
					.forEach(({ column }) => {
						const c = cols.find(c => c.id && c.id === column.name);
						if (c) newC.push(c);
					});
				newC.push(cols[cols.length - 1]);
				return newC;
			}

			return cols.filter(c => {
				const col = props.allCompaniesColumnsAndAdditionalColumns.find(c2 => c2.name === c.id);
				return !col || !col.admin || isAdmin;
			});
		},
		[additionalColumns, additionalFieldColumns, JSON.stringify({ companies: companies.current, sortColumns, statuses, users, hidableColumns, selectedColumnEventType, loadingState, selectedExternalMappingId: selectedExternalMappingId.company, atomExternalMapping })]
	);

	const selectedLength = selectedCompanies.filter(c => isAllSelected ? !c.checked : c.checked).length;
	const onClickFilter = React.useCallback((id: FilterId) => {
		setSelectedFilter(id);
		setOpenSummary(false);
		setFilterOpen(true);
	}, []);

	const localEnrichCompanies = (replace: boolean) => {
		enrichCompanies({
			companies: selectedCompanies.filter(c => isAllSelected ? !c.checked : c.checked).map(c => c.id),
			all: isAllSelected,
			new_filters: filterResult.formatted,
			search: searchName,
			replace
		})
			.then(({ rejected, changed, rejected_values }) => {
				updateCompanies(true);
				setInformationModalState({
					isOpen: true,
					data: {
						hideButton: true,
						content: <GeoPopupContent
							rejected={rejected}
							changed={changed}
							rejected_values={rejected_values.map(({ company, items }) => ({ company, items: items?.sort(sortItems) }))}
							setTitle={title => setInformationModalState(state => state.data ? ({ ...state, data: { ...state.data, title } }) : ({ ...state }))} />,
						title: <><Translate id='enrichment' />{rejected_values.length > 0 ? ` (1 / ${(rejected_values.length)})` : ''}</>
					}
				});
			})
			.catch(e => {
				console.error(e);
				setLoadingState(LoadingStateEnum.ERROR);
			});
	};

	function getRecords() {
		return isAllSelected
			? rawCompanies.total - selectedCompanies.filter(c => !c.checked).length
			: selectedCompanies.filter(c => c.checked).length;
	}

	const hasSearchOrFilters = (searchName?.trim().length ?? 0) > 0 || filterTreeLength(filterResult?.formatted) > 0;

	return <>
		{companies.current.length > 0 ?
			<Container>
				<React.Suspense fallback={<FlexDiv height='calc(100% - 44px)' width='100%' justify='center'><PageLoader /></FlexDiv>}>
					<Table
						onSortClick={id => {
							const value = filterIdToTableSortColumn(id);
							if (value === undefined) return;
							const existingOrderBy = orderBys.findIndex(ob => compareSimilarFilters(ob.order_by, id));
							if (existingOrderBy !== -1) {
								setOrderBys(prev => prev.map((ob, index) => index === existingOrderBy ? { ...ob, descending: !ob.descending } : ob));
								return;
							}
							const orderBy: OrderBy<typeof props['allColumns'][number]> = { value, order_by: id, descending: false, additionalValue: value.additionalDropdownData?.[0]?.value };
							return setOrderBys(prev => [...prev, orderBy]);
						}}
						containerBorder={props.isPopup}
						innerRef={tableRef}
						height='calc(100% - 44px)'
						columns={columns}
						data={companies.current}
						EnableResize
						hiddenColumns={hiddenColumns}
						onClickFilter={onClickFilter}
						onResize={(value) => Object.keys(value).length > 0 && setTableResize(value)}
						resizeValue={tableResize}
						isInformationHeaderOpen={props.isPopup && (selectedCompanies.some(c => c.checked) || isAllSelected)}
						informationHeader={<SelectAll
							onMaxSelected={setIsAllSelected}
							selected={selectedLength}
							max={rawCompanies.total}
							isAllSelected={isAllSelected}
						/>}
					/>
				</React.Suspense>
				<Pagination
					label={translate('company.beta').toString()}
					amount={rawCompanies.total}
					currentCount={rawCompanies.companies.length}
					steps={[DEFAULT_STEP, 50]}
					onChange={(value) => {
						setPagination(value);
					}}
				/>
			</Container>
			: hasSearchOrFilters
				? <NoData
					isNoResult
					message={translateToString('No results found')}
					messageButton={<Translate id='reset_filters' />}
					onClickButton={props.onResetFilters}
				/>
				: <NoData
					message={translateToString('no_companies')}
					messageButton={<Translate id='general.import' />}
					messageButton2={<Translate id='export_model' />}
					onClickButton={() => history.push({ pathname: '/settings', hash: 'import' })}
					onClickButton2={() => {
						const a = document.createElement('a');
						a.href = getLanguageCode() === 'fr' ? CompaniesTemplateFr : CompaniesTemplateEn;
						a.download = 'companies_template.csv';
						a.click();
					}} />
		}
		<ClientCompany
			isOpen={isOpen}
			setOpen={(b: boolean) => {
				if (!b) { removeAllUrlParameters(); }
				setOpen(b);
			}}

			clientCompanyId={clientCompanyId!}
			fullOpenMode={isFullOpenMode}
		/>
		<ClientCompanyCreation
			isOpen={isCreationOpen}
			setOpen={setCreationOpen}
			onCreate={(value) => {
				setClientCompanyId(value.company_id);
				updateCompanies(true);
				setFullOpenMode(true);
				setOpen(true);
			}}
			company={selectedDuplicateCompany}
		/>
		<ComponentLoader loadingState={loadingState} allScreen />
		<Popup
			isOpen={isBulkEditOpen || selectedEventType !== undefined}
			popupMode={PopupMode.Details}
			onClickOut={() => {
				setIsBulkEditOpen(false);
				setSelectedEventType(undefined);
			}}
			disableOutClick={isParent}
			popupStyle={{ animate: true, height: '100%', top: '0%' }}
		>
			<PopupCreation
				title={isBulkEditOpen ? <Translate id='mass_modification' /> : <><Translate id='map.modal_right.plan_your' /><Translate id={`event.${selectedEventType?.name}_s`} /></>}
				hideValidation
				onClose={() => {
					setIsBulkEditOpen(false);
					setSelectedEventType(undefined);
				}}
			>
				{(selectedEventType != null)
					? <CompanyBulkEventCreation
						type={selectedEventType}
						onClose={() => setSelectedEventType(undefined)}
						ids={selectedCompanies.filter(c => isAllSelected ? !c.checked : c.checked).map(c => c.id)}
						isAll={isAllSelected}
						body={{
							new_filters: filterResult?.formatted,
							search: searchName
						}}
						onChildChange={setIsParent}
					/>
					: <CompanyBulkEdition
						setLoadingState={setLoadingState}
						ids={selectedCompanies.filter(c => isAllSelected ? !c.checked : c.checked).map(c => c.id)}
						users={users?.map(u => ({ label: u.name, value: u, image: u.photoUrl }))}
						statuses={statuses?.map((s, i) => { return { label: s.name, value: s, color: s.color_code, selected: i === 0 }; })}
						tags={tags}
						records={getRecords()}
						refresh={() => {
							setSelectedCompanies([]);
							setIsAllSelected(false);
							updateCompanies(true);
						}}
						onClose={() => setIsBulkEditOpen(false)}
						isAll={isAllSelected}
						new_filters={filterResult?.formatted}
						search={searchName}
					/>
				}
			</PopupCreation>
		</Popup>
		<PopupDeletion
			isOpen={isBulkDeleteOpen}
			onClickOut={() => setIsBulkDeleteOpen(false)}
			records={getRecords()}
			onValidation={() => {
				deleteCompanies(selectedCompanies.filter(c => isAllSelected ? !c.checked : c.checked).map(c => c.id), isAllSelected, filterResult.formatted, searchName)
					.then(response => {
						if (response) {
							updateCompanies(true);
						}
					});
				setSelectedCompanies([]);
				setIsAllSelected(false);
			}}
			translationKey='companies'
		/>
		<AddressModificator
			isOpen={isModifyAddressOpen}
			setOpen={setModifyAddressOpen}
			defaultAddress={(modifyAddress != null) ? {
				country: modifyAddress.company_country,
				postalCode: modifyAddress.company_post_code,
				town: modifyAddress.company_city,
				street: modifyAddress.company_address
			} : undefined}
			onChange={(na) => {
				const company = companies.current.find(c => c.company_id === modifyAddress?.company_id);
				if (company != null) {
					company.company_address = na.street && na.street.length > 0 ? na.street : undefined;
					company.company_post_code = na.postalCode && na.postalCode.length > 0 ? na.postalCode : undefined;
					company.company_city = na.town && na.town.length > 0 ? na.town : undefined;
					company.company_country = na.country && na.country.length > 0 ? na.country : undefined;
					company.latitude = na.latitude ? na.latitude : undefined;
					company.longitude = na.longitude ? na.longitude : undefined;
					company.updated_at = new Date();
					company.updated_by = cookie.get('user_id');

					putCompany({
						client_company_id: company.company_id,
						billing_city: company.company_city,
						billing_country: company.company_country,
						billing_postcode: company.company_post_code,
						billing_address: company.company_address,
						latitude: company.latitude,
						longitude: company.longitude
					}).then(response => {
						if (response) {
							setCompanies(companies.current.map(c => c.company_id === company.company_id ? company : c));
						}
					});
				}
			}}
			title={translate('company.columns.company_address').toString()}
		/>
		<PopupInformation
			height='fit-content'
			gap='20px'
			popupStyle={{ fitContent: true, width: '50vw', minWidth: '1000px' }}
			isOpen={informationModalState.isOpen}
			onClickOut={() => setInformationModalState({ isOpen: false })}
			title={informationModalState.data?.title}
			hideButton={informationModalState.data?.hideButton}
		>
			{informationModalState.data?.content}
		</PopupInformation>
		<PopupEvent
			isOpen={eventModalState.isOpen}
			setIsOpen={isOpen => setEventModalState({ isOpen })}
			defaultValues={{ clientCompanyId: eventModalState.data, eventTypeId: selectedColumnEventType }}
		/>
		{!props.isPopup && (selectedCompanies.some(c => c.checked) || isAllSelected) && <BottomActionButtons
			createEvents={setSelectedEventType}
			enrich={() => setIsEnrichOpen(true)}
			edit={() => setIsBulkEditOpen(true)}
			delete={() => setIsBulkDeleteOpen(true)}
			selectAll={() => setIsAllSelected(true)}
			isAllSelected={isAllSelected}
			selectedRows={selectedLength}
			maxSelectedRows={rawCompanies.total}
			removeSelection={() => setIsAllSelected(false)} />}
		<PopupInformation
			isOpen={isEnrichOpen}
			onClickOut={() => setIsEnrichOpen(false)}
			title={<Translate id='enrich_geo_positions_of_companies' />}
			popupStyle={{ width: '750px', height: '300px' }}
			hideButton
			additionalButton={[
				{
					title: <Translate id='companies_without_positions' />,
					onSubmit: () => {
						setLoadingState(LoadingStateEnum.LOADING);
						setIsAllSelected(false);
						localEnrichCompanies(false);
						setIsEnrichOpen(false);
					},
					canValidate: true,
				},
				{
					title: <Translate id='all_selected_companies' />,
					onSubmit: () => {
						setLoadingState(LoadingStateEnum.LOADING);
						setIsAllSelected(false);
						localEnrichCompanies(true);
						setIsEnrichOpen(false);
					},
					canValidate: true
				},
				{
					title: <Translate id='back' />,
					onSubmit: () => setIsEnrichOpen(false),
					canValidate: true,
					style: ButtonStyle.White
				}
			]}
		>
			<DefaultTextDiv fontSize='14px'>
				<Translate id='you_are_about_to_enrich' />
				<br />
				<Translate id='wich_companies_do_you_want_to_enrich' />
			</DefaultTextDiv>
		</PopupInformation>
		<InstancePopup modalState={formModalState} isOpen={formModalState.isOpen}
			onClickOut={() => setFormModalState({ ...formModalState, isOpen: false, })} />
	</>;
}

export function SelectAll(props: {
	selected: number
	max: number
	onMaxSelected: (b: boolean) => void
	isAllSelected: boolean
}): JSX.Element {
	const { selected, max, onMaxSelected, isAllSelected } = props;
	const translate = getTranslate(storeLang.getState().localize);

	return (
		<SelectAllDiv>
			<img
				style={{ cursor: 'pointer', width: '18px' }}
				src={close}
				onClick={() => onMaxSelected(false)}
			/>
			<FlexDiv gap='5px'>
				{isAllSelected
					? <>
						{translate('{{NUMBER}}_records_selected').toString().replace('{{NUMBER}}', (max - selected).toString())}
						<Link onClick={() => onMaxSelected(false)}>
							<Translate id='clear_selection' />
						</Link>
					</>
					: <>
						{translate('{{NUMBER}}_records_selected').toString().replace('{{NUMBER}}', selected.toString())}
						<Link onClick={() => onMaxSelected(true)}>
							{translate('select_all_{{NUMBER}}_records').toString().replace('{{NUMBER}}', max.toString())}
						</Link>
					</>
				}
			</FlexDiv>
		</SelectAllDiv>
	);
}
